import { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography } from "@mui/material";

import { useNotification } from "../../../../Hooks/useNotification";
import CustomLoadingOverlay from "../../../../Components/CustomLoadingOverlay";

import { getColumns } from "./columns";
import OrderDetailsModal from "./OrderDetailsModal";

// Jobs table for a single expanded driver accordion. The jobs are now
// embedded directly in the job-driver-list/{tab} (or today-job-driver-list)
// response as driver.associated_job_list - no separate per-driver API call.
const DriverJobsTable = ({ driver, activeTab, period = "AM", onRefresh, onPageChange }) => {
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedInvoiceNumbers, setSelectedInvoiceNumbers] = useState("");
  const [associatedJobList, setAssociatedJobList] = useState(driver.associated_job_list);
  const [pageLoading, setPageLoading] = useState(false);

  const showNotification = useNotification();

  // Re-sync if the parent re-fetches the drivers list from scratch (e.g. a
  // filter/tab change), so we don't keep showing a stale page.
  useEffect(() => {
    setAssociatedJobList(driver.associated_job_list);
  }, [driver.associated_job_list]);

  // associated_job_list.data/pagination are both keyed by job period
  // (AM / M / PM) - each period paginates independently.
  const jobs = associatedJobList?.data?.[period] || [];
  const pagination = associatedJobList?.pagination?.[period];

  const handlePaginationModelChange = async (model) => {
    const requestedPage = model.page + 1;
    const requestedPerPage = model.pageSize;

    if (
      requestedPage === (pagination?.current_page || 1) &&
      requestedPerPage === (pagination?.per_page || 20)
    ) {
      return;
    }

    if (!onPageChange) return;

    setPageLoading(true);
    try {
      const updatedJobList = await onPageChange(driver, period, requestedPage, requestedPerPage);
      if (updatedJobList) {
        setAssociatedJobList(updatedJobList);
      }
    } finally {
      setPageLoading(false);
    }
  };

  const rows = useMemo(
    () =>
      jobs.map((job, index) => ({
        ...job,
        id: job.job_id || index,
      })),
    [jobs]
  );

  // Rows for returned products (is_return_job === 1) get their whole row
  // colored using that row's own return_job_color (hex from the API).
  const returnJobColor = rows.find(
    (row) => row.is_return_job === 1 && row.return_job_color
  )?.return_job_color;

  // Same idea for partially delivered jobs (is_partial_job === 1), using
  // that row's own partial_job_color.
  const partialJobColor = rows.find(
    (row) => row.is_partial_job === 1 && row.partial_job_color
  )?.partial_job_color;

  const getRowClassName = (params) => {
    const classes = [];
    if (params.row.is_return_job === 1) {
      classes.push(`return-job-row-${params.row.id}`);
    }
    if (params.row.is_partial_job === 1) {
      classes.push(`partial-job-row-${params.row.id}`);
    }
    return classes.join(" ");
  };

  const handleRowClick = (params) => {
    const row = params.row;
    setSelectedJobId(row.job_id);

    if (row.related_job_list && row.related_job_list.length > 0) {
      setSelectedInvoiceNumbers(row.related_job_list.join(","));
      setOrderDetailsOpen(true);
      return;
    }

    const invoiceNumber = row.invoice_number;
    if (invoiceNumber) {
      setSelectedInvoiceNumbers(invoiceNumber);
      setOrderDetailsOpen(true);
    } else {
      showNotification("error", "No invoice number found for this order");
    }
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsOpen(false);
    setSelectedJobId(null);
    setSelectedInvoiceNumbers("");
  };

  const columns = getColumns(activeTab);

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Jobs - {driver.full_name}
        </Typography>
        {returnJobColor && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "2px",
                backgroundColor: returnJobColor,
                border: "1px solid rgba(0,0,0,0.15)",
              }}
            />
            <Typography sx={{ fontSize: "0.8rem", color: "#667085" }}>
              this color represents returned job
            </Typography>
          </Box>
        )}
        {partialJobColor && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "2px",
                backgroundColor: partialJobColor,
                border: "1px solid rgba(0,0,0,0.15)",
              }}
            />
            <Typography sx={{ fontSize: "0.8rem", color: "#667085" }}>
              this color represents partial delivery
            </Typography>
          </Box>
        )}
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
        onRowClick={handleRowClick}
        getRowClassName={getRowClassName}
        loading={pageLoading}
        paginationMode="server"
        rowCount={pagination?.total || rows.length}
        paginationModel={{
          page: (pagination?.current_page || 1) - 1,
          pageSize: pagination?.per_page || 20,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        slots={{
          loadingOverlay: CustomLoadingOverlay,
        }}
        disableColumnMenu
        sx={{
          backgroundColor: "#fff",
          "& .MuiDataGrid-cell": {
            fontSize: "14px",
            fontWeight: 500,
            color: "#48505E",
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#d6d6d6ff !important",
            color: "#fff !important",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            color: "#242424ff !important",
          },
          "& .MuiDataGrid-columnSeparator": {
            color: "rgba(177, 177, 177, 1)",
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
        pageSizeOptions={[20, 50, 100]}
      />

      {/* Dynamic per-row background color for returned-product and
          partially-delivered jobs, since each row's return_job_color /
          partial_job_color is an arbitrary hex value from the API and
          can't be expressed via the static sx object above. */}
      <style>
        {rows
          .filter((row) => row.is_return_job === 1 && row.return_job_color)
          .map((row) => {
            const className = `return-job-row-${row.id}`;
            return `
              .MuiDataGrid-row.${className} {
                background-color: ${row.return_job_color} !important;
              }
              .MuiDataGrid-row.${className}:hover {
                background-color: ${row.return_job_color} !important;
              }
              .MuiDataGrid-row.${className}.Mui-selected {
                background-color: ${row.return_job_color} !important;
              }
            `;
          })
          .join("")}
        {rows
          .filter((row) => row.is_partial_job === 1 && row.partial_job_color)
          .map((row) => {
            const className = `partial-job-row-${row.id}`;
            return `
              .MuiDataGrid-row.${className} {
                background-color: ${row.partial_job_color} !important;
              }
              .MuiDataGrid-row.${className}:hover {
                background-color: ${row.partial_job_color} !important;
              }
              .MuiDataGrid-row.${className}.Mui-selected {
                background-color: ${row.partial_job_color} !important;
              }
            `;
          })
          .join("")}
      </style>

      {orderDetailsOpen && (
        <OrderDetailsModal
          open={orderDetailsOpen}
          onClose={handleCloseOrderDetails}
          invoiceNumbers={selectedInvoiceNumbers}
          jobId={selectedJobId}
          allDrivers={[]}
          onRefresh={() => onRefresh?.()}
        />
      )}
    </>
  );
};

export default DriverJobsTable;
